import * as React from "react";

import Input from "@/components/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import i18n from "@/i18n";
import { cn } from "@/utils";
import { localeFor } from "@/utils/formatters";

interface MonthYearPickerProps {
  label?: string;
  /** "YYYY-MM" or undefined. */
  value?: string;
  /** Receives "YYYY-MM" or undefined. */
  onChange: (value: string | undefined) => void;
  placeholder?: string;
  disabled?: boolean;
  hint?: string;
  state?: "default" | "error";
}

const YYYY_MM_RE = /^\d{4}-(0[1-9]|1[0-2])$/;
const MONTH_COUNT = 12;
const YEAR_RANGE = 60;

function parse(value: string | undefined): { year: number; month: number } | undefined {
  if (!value || !YYYY_MM_RE.test(value)) return undefined;
  const [y, m] = value.split("-").map(Number);
  return { year: y, month: m };
}

function toWire(year: number, month: number): string {
  return `${year}-${String(month).padStart(2, "0")}`;
}

function formatDisplay(value: string | undefined): string {
  const parsed = parse(value);
  if (!parsed) return "";
  const d = new Date(parsed.year, parsed.month - 1, 1);
  return d.toLocaleDateString(localeFor(i18n.language || "en"), {
    month: "short",
    year: "numeric",
  });
}

function getMonthShortNames(locale: string): Array<string> {
  return Array.from({ length: MONTH_COUNT }, (_, i) =>
    new Date(2000, i, 1).toLocaleDateString(locale, { month: "short" }),
  );
}

/**
 * Month + year picker that emits a "YYYY-MM" string. Shows a year scroller and
 * a 12-month grid so the user picks a month and year directly — no day grid.
 */
export function MonthYearPicker({
  label,
  value,
  onChange,
  placeholder,
  disabled,
  hint,
  state,
}: MonthYearPickerProps) {
  const locale = localeFor(i18n.language || "en");
  const parsed = parse(value);
  const today = React.useMemo(() => new Date(), []);
  const [open, setOpen] = React.useState(false);
  const [viewYear, setViewYear] = React.useState<number>(
    parsed?.year ?? today.getFullYear(),
  );

  React.useEffect(() => {
    if (open) {
      setViewYear(parse(value)?.year ?? today.getFullYear());
    }
  }, [open, value, today]);

  const monthNames = React.useMemo(() => getMonthShortNames(locale), [locale]);
  const minYear = today.getFullYear() - YEAR_RANGE;
  const maxYear = today.getFullYear();

  const handlePickMonth = (monthIndex: number) => {
    const month = monthIndex + 1;
    if (
      viewYear > maxYear ||
      (viewYear === today.getFullYear() && month > today.getMonth() + 1) ||
      viewYear < minYear
    ) {
      return;
    }
    onChange(toWire(viewYear, month));
    setOpen(false);
  };

  const display = formatDisplay(value);

  return (
    <Popover open={open} onOpenChange={(o) => !disabled && setOpen(o)}>
      <PopoverTrigger asChild>
        <div className="w-full">
          <Input
            label={label}
            placeholder={placeholder}
            value={display}
            disabled={disabled}
            readOnly
            hint={hint}
            state={state}
            onClick={() => !disabled && setOpen(true)}
            onKeyDown={(e) => {
              if (e.key === "ArrowDown" || e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                setOpen(true);
              }
            }}
            className="cursor-pointer"
          />
        </div>
      </PopoverTrigger>
      <PopoverContent
        className="w-[280px] p-3 bg-white text-garnet border-garnet/20"
        align="start"
      >
        <div className="flex items-center justify-between mb-3">
          <button
            type="button"
            aria-label="Previous year"
            onClick={() => setViewYear((y) => Math.max(minYear, y - 1))}
            disabled={viewYear <= minYear}
            className="h-8 w-8 inline-flex items-center justify-center rounded hover:bg-garnet/10 disabled:opacity-30 text-garnet"
          >
            ‹
          </button>
          <span className="font-semibold text-garnet body">{viewYear}</span>
          <button
            type="button"
            aria-label="Next year"
            onClick={() => setViewYear((y) => Math.min(maxYear, y + 1))}
            disabled={viewYear >= maxYear}
            className="h-8 w-8 inline-flex items-center justify-center rounded hover:bg-garnet/10 disabled:opacity-30 text-garnet"
          >
            ›
          </button>
        </div>
        <div className="grid grid-cols-3 gap-2">
          {monthNames.map((name, i) => {
            const month = i + 1;
            const isSelected =
              parsed?.year === viewYear && parsed.month === month;
            const isFuture =
              viewYear > today.getFullYear() ||
              (viewYear === today.getFullYear() && month > today.getMonth() + 1);
            return (
              <button
                key={name}
                type="button"
                onClick={() => handlePickMonth(i)}
                disabled={isFuture}
                aria-pressed={isSelected}
                className={cn(
                  "text-sm py-2 rounded transition-colors",
                  isSelected
                    ? "bg-garnet text-white"
                    : "text-garnet hover:bg-garnet/10",
                  isFuture && "opacity-30 cursor-not-allowed hover:bg-transparent",
                )}
              >
                {name}
              </button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}
